import api from './api';

const buildQuery = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') {
      query.set(key, value);
    }
  });
  return query.toString();
};

// reviews 관련 API
export const reviewsApi = {
  // 신규 발매 조회 (전체/앨범/싱글)
  getNewReleases: async (
    sort = 'latest',
    page = 1,
    size = 60,
    period = 'week',
    region = null,
    type = null,
  ) => {
    const query = buildQuery({ type, sort, page, size, period, region });
    const response = await api.getPublic(`/albums/new-releases?${query}`);
    return response.data;
  },

  // 신규 앨범 조회
  getNewAlbums: async (sort = 'popular', page = 1, size = 60, period = 'week', region = null) => {
    return reviewsApi.getNewReleases(sort, page, size, period, region, 'albums');
  },

  excludeNewRelease: async (albumIdsToExclude) => {
    const response = await api.putAuth('/albums/new-releases', albumIdsToExclude);
    return response.data;
  },

  excludeRecommends: async (albumIdsToExclude) => {
    const response = await api.putAuth(`/albums/recommend`, albumIdsToExclude);
    return response.data;
  },

  // 신규 싱글 조회
  getNewSingles: async (sort = 'popular', page = 1, size = 60, period = 'week', region = null) => {
    return reviewsApi.getNewReleases(sort, page, size, period, region, 'singles');
  },

  // 추천 앨범 조회 (전체)
  getRecommend: async (sort = 'popular', page = 1, size = 60, period = null, region = null) => {
    const query = buildQuery({ sort, page, size, region });
    const response = await api.getPublic(`/albums/recommend?${query}`);
    return response.data;
  },

  // 추천 앨범 조회
  getRecommendAlbums: async (
    sort = 'popular',
    page = 1,
    size = 60,
    period = null,
    region = null,
  ) => {
    const query = buildQuery({ type: 'albums', sort, page, size, region });
    const response = await api.getPublic(`/albums/recommend?${query}`);
    return response.data;
  },

  // 추천 싱글 조회
  getRecommendSingles: async (
    sort = 'popular',
    page = 1,
    size = 60,
    period = null,
    region = null,
  ) => {
    const query = buildQuery({ type: 'singles', sort, page, size, region });
    const response = await api.getPublic(`/albums/recommend?${query}`);
    return response.data;
  },

  // 최근 리뷰 조회 (전체/앨범/싱글)
  getRecentReviews: async (
    sort = 'latest',
    page = 1,
    size = 60,
    period = 'all',
    region = null,
    type = null,
  ) => {
    const query = buildQuery({ type, sort, page, size, period, region });
    const response = await api.getPublic(`/albums/recent-reviews?${query}`);
    return response.data;
  },

  // 인기 리뷰 조회
  getPopularReviews: async (sort = 'likes', page = 1, size = 60, period = 'all', region = null) =>
    reviewsApi.getRecentReviews(sort || 'likes', page, size, period, region),

  // 최근 리뷰 조회 (앨범)
  getRecentReviewsAlbums: async (
    sort = 'latest',
    page = 1,
    size = 60,
    period = null,
    region = null,
  ) => {
    return reviewsApi.getRecentReviews(sort, page, size, period, region, 'albums');
  },

  // 최근 리뷰 조회 (싱글)
  getRecentReviewsSingles: async (
    sort = 'latest',
    page = 1,
    size = 60,
    period = null,
    region = null,
  ) => {
    return reviewsApi.getRecentReviews(sort, page, size, period, region, 'singles');
  },

  // 한국 음악 리뷰 앨범 조회
  getKoreanMusicAlbums: async (sort = 'popular', page = 1, size = 60) => {
    const response = await api.getPublic(
      `/albums/reviewed-region?region=kr&type=albums&sort=${sort}&page=${page}&size=${size}`,
    );
    return response.data;
  },

  // 한국 음악 리뷰 싱글 조회
  getKoreanMusicSingles: async (sort = 'popular', page = 1, size = 60) => {
    const response = await api.getPublic(
      `/albums/reviewed-region?region=kr&type=singles&sort=${sort}&page=${page}&size=${size}`,
    );
    return response.data;
  },

  // 글로벌 음악 리뷰 앨범 조회
  getGlobalMusicAlbums: async (sort = 'popular', page = 1, size = 60) => {
    const response = await api.getPublic(
      `/albums/reviewed-region?region=global&type=albums&sort=${sort}&page=${page}&size=${size}`,
    );
    return response.data;
  },

  // 글로벌 음악 리뷰 싱글 조회
  getGlobalMusicSingles: async (sort = 'popular', page = 1, size = 60) => {
    const response = await api.getPublic(
      `/albums/reviewed-region?region=global&type=singles&sort=${sort}&page=${page}&size=${size}`,
    );
    return response.data;
  },

  // Background refresh reads must not count as another album visit.
  getAlbumTrackRefresh: async (albumId) => {
    const response = await api.getAuth(`/albums/${albumId}/track-refresh`);
    return response.data;
  },

  // 앨범 상세 조회
  getAlbumDetail: async (albumId, backgroundTracks = false) => {
    const response = await api.getAuth(
      `/albums/${albumId}${backgroundTracks ? '?trackRefresh=background' : ''}`,
    );
    return response.data;
  },

  // 애플 뮤직 앨범 상세 조회
  getAppleMusicAlbumDetail: async (sourceId) => {
    const response = await api.getAuth(`/albums?type=apple_music&id=${sourceId}`);
    return response.data;
  },

  getAlbumDetailBySource: async (sourceId, albumSource, backgroundTracks = false) => {
    const response = await api.getAuth(
      `/albums?type=${albumSource}&id=${encodeURIComponent(sourceId)}${backgroundTracks ? '&trackRefresh=background' : ''}`,
    );
    return response.data;
  },

  // 앨범 리뷰 조회
  getAlbumReviews: async (albumId) => {
    const response = await api.getAuth(`/albums/${albumId}/reviews`);
    return response.data;
  },

  // sourceId로 앨범 리뷰 조회
  getAlbumReviewsBySource: async (sourceId, albumSource) => {
    const response = await api.getAuth(
      `/albums/reviews?type=${albumSource.toLowerCase()}&id=${sourceId}`,
    );
    return response.data;
  },

  // 리뷰 ID로 단일 리뷰 조회
  getReviewById: async (reviewId) => {
    const response = await api.getAuth(`/albums/reviews/${reviewId}`);
    return response.data;
  },

  // 리뷰 작성
  postReview: async (albumId, content, rating) => {
    const response = await api.postAuth(`/albums/${albumId}/reviews`, { content, rating });
    return response.data;
  },

  postAppleMusicReview: async (sourceId, content, rating, otherVersionSourceIds = []) => {
    const response = await api.postAuth(`/albums/reviews?type=apple_music&id=${sourceId}`, {
      content,
      rating,
      otherVersionSourceIds,
    });
    return response.data;
  },

  // 리뷰 수정
  updateReview: async (albumId, reviewId, content, rating) => {
    const response = await api.putAuth(`/albums/${albumId}/reviews/${reviewId}`, {
      content,
      rating,
    });
    return response.data;
  },

  // 리뷰 핀 추가
  designateReview: async (reviewId, designationType) => {
    const response = await api.postAuth(`/designated-reviews/${designationType}`, { reviewId });
    return response.data;
  },

  // 리뷰 핀 삭제
  undesignateReview: async (reviewId, designationType) => {
    const response = await api.deleteAuth(`/designated-reviews/${designationType}/${reviewId}`);
    return response.data;
  },

  // 리뷰 삭제
  deleteReview: async (albumId, reviewId) => {
    const response = await api.deleteAuth(`/albums/${albumId}/reviews/${reviewId}`);
    return response.data;
  },

  // 리뷰 북마크 추가
  addBookmark: async (sourceId, albumSource) => {
    if (albumSource === 'LOCAL') {
      const response = await api.postAuth('/bookmarks', {
        itemType: 'ALBUM',
        itemId: Number(sourceId),
      });
      return response.data;
    } else {
      const response = await api.postAuth('/bookmarks', {
        itemType: 'ALBUM',
        sourceId: sourceId,
        albumSource: albumSource,
      });
      return response.data;
    }
  },

  // 리뷰 북마크 삭제
  deleteBookmark: async (albumId) => {
    const response = await api.deleteAuth(`/bookmarks/album/${albumId}`);
    return response.data;
  },

  // 회원 리뷰 스크랩 추가
  addReviewBookmark: async (reviewId) => {
    const response = await api.postAuth('/bookmarks', {
      itemType: 'ALBUM_REVIEW',
      itemId: Number(reviewId),
    });
    return response.data;
  },

  // 회원 리뷰 스크랩 삭제
  deleteReviewBookmark: async (reviewId) => {
    const response = await api.deleteAuth(`/bookmarks/album_review/${reviewId}`);
    return response.data;
  },

  // 리뷰 좋아요 추가
  addLike: async (reviewId) => {
    const response = await api.postAuth('/likes', {
      itemType: 'ALBUM_REVIEW',
      itemId: Number(reviewId),
    });
    return response.data;
  },

  // 리뷰 좋아요 삭제
  deleteLike: async (reviewId) => {
    const response = await api.deleteAuth(`/likes/album_review/${reviewId}`);
    return response.data;
  },

  // 리뷰 댓글 생성
  addComment: async (reviewId, content, parentId = null, mentionedUserId = null) => {
    const payload = {
      articleType: 'ALBUM_REVIEW',
      articleId: Number(reviewId),
      content,
    };

    if (parentId !== null) {
      payload.parentId = parentId;
    }
    if (mentionedUserId !== null) {
      payload.mentionedUserId = mentionedUserId;
    }

    const response = await api.postAuth('/comments', payload);
    return response.data;
  },

  // 리뷰 댓글 삭제
  deleteComment: async (commentId) => {
    const response = await api.deleteAuth(`/comments/${commentId}`);
    return response.data;
  },

  // 댓글 수정
  updateComment: async (commentId, content) => {
    const response = await api.putAuth(`/comments/${commentId}`, { content });
    return response.data;
  },

  // 댓글 좋아요 추가
  addCommentLike: async (commentId) => {
    const response = await api.postAuth('/likes', {
      itemType: 'COMMENT',
      itemId: Number(commentId),
    });
    return response.data;
  },

  // 댓글 좋아요 삭제
  deleteCommentLike: async (commentId) => {
    const response = await api.deleteAuth(`/likes/comment/${commentId}`);
    return response.data;
  },
};
